import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  TestTube,
  Shield,
  Lock,
  Gauge,
  Fingerprint,
  Webhook,
  Globe,
  KeyRound,
  Zap,
  Loader2,
  CheckCircle2,
  XCircle,
  Play,
  AlertCircle,
} from "lucide-react";

// ==================== TYPES ====================

interface TestResult {
  passed?: boolean;
  success?: boolean;
  message?: string;
  results?: any[];
  summary?: any;
  [key: string]: any;
}

interface TestConfig {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  endpoint: string;
  color: string;
  bgColor: string;
}

// ==================== CONSTANTS ====================

const TESTS: TestConfig[] = [
  {
    id: "auth",
    title: "Authentication",
    description: "Verify API key authentication flow works correctly",
    icon: Lock,
    endpoint: "/api/admin/partner-api/test-auth",
    color: "text-blue-500",
    bgColor: "bg-blue-500/10",
  },
  {
    id: "scopes",
    title: "Scope Enforcement",
    description: "Verify API key scopes are properly enforced",
    icon: Shield,
    endpoint: "/api/admin/partner-api/test-scopes",
    color: "text-green-500",
    bgColor: "bg-green-500/10",
  },
  {
    id: "rate-limit",
    title: "Rate Limiting",
    description: "Verify per-key rate limits are correctly applied",
    icon: Gauge,
    endpoint: "/api/admin/partner-api/test-rate-limit",
    color: "text-amber-500",
    bgColor: "bg-amber-500/10",
  },
  {
    id: "idempotency",
    title: "Idempotency",
    description: "Verify idempotency key handling for safe retries",
    icon: Fingerprint,
    endpoint: "/api/admin/partner-api/test-idempotency",
    color: "text-purple-500",
    bgColor: "bg-purple-500/10",
  },
  {
    id: "webhook-signing",
    title: "Webhook Signing",
    description: "Verify HMAC webhook signature generation and verification",
    icon: Webhook,
    endpoint: "/api/admin/partner-api/test-webhook-signing",
    color: "text-emerald-500",
    bgColor: "bg-emerald-500/10",
  },
  {
    id: "ip-allowlist",
    title: "IP Allowlist",
    description: "Verify IP-based access control enforcement",
    icon: Globe,
    endpoint: "/api/admin/partner-api/test-ip-allowlist",
    color: "text-rose-500",
    bgColor: "bg-rose-500/10",
  },
  {
    id: "key-lifecycle",
    title: "Key Lifecycle",
    description: "Verify create, rotate, and revoke key operations",
    icon: KeyRound,
    endpoint: "/api/admin/partner-api/test-key-lifecycle",
    color: "text-cyan-500",
    bgColor: "bg-cyan-500/10",
  },
  {
    id: "e2e",
    title: "End-to-End",
    description: "Full integration test covering all partner API endpoints",
    icon: Zap,
    endpoint: "/api/admin/partner-api/test-e2e",
    color: "text-orange-500",
    bgColor: "bg-orange-500/10",
  },
];

// ==================== COMPONENT ====================

export default function SecurityTestsPage() {
  const { toast } = useToast();
  const [results, setResults] = useState<Record<string, TestResult>>({});
  const [runningTests, setRunningTests] = useState<Set<string>>(new Set());
  const [isRunningAll, setIsRunningAll] = useState(false);

  // Create a mutation for running individual tests
  const runTestMutation = useMutation({
    mutationFn: async ({ id, endpoint }: { id: string; endpoint: string }) => {
      setRunningTests((prev) => new Set(prev).add(id));
      const response = await apiRequest("POST", endpoint);
      return { id, result: await response.json() };
    },
    onSuccess: ({ id, result }) => {
      setResults((prev) => ({ ...prev, [id]: result }));
      setRunningTests((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      const passed = result.passed ?? result.success ?? false;
      toast({
        title: passed ? "Test Passed" : "Test Failed",
        description: result.message || `${TESTS.find((t) => t.id === id)?.title} test completed`,
        variant: passed ? "default" : "destructive",
      });
    },
    onError: (error, { id }) => {
      setResults((prev) => ({
        ...prev,
        [id]: { passed: false, message: String(error) },
      }));
      setRunningTests((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      toast({
        title: "Test Error",
        description: String(error),
        variant: "destructive",
      });
    },
  });

  async function runAllTests() {
    setIsRunningAll(true);
    setResults({});
    for (const test of TESTS) {
      try {
        await runTestMutation.mutateAsync({ id: test.id, endpoint: test.endpoint });
      } catch {
        // Error already handled in onError
      }
    }
    setIsRunningAll(false);
  }

  function getTestStatus(testId: string) {
    if (runningTests.has(testId)) return "running";
    if (results[testId]) {
      const r = results[testId];
      return (r.passed ?? r.success ?? false) ? "passed" : "failed";
    }
    return "idle";
  }

  // Summary counts
  const passedCount = Object.values(results).filter((r) => r.passed ?? r.success ?? false).length;
  const failedCount = Object.values(results).filter((r) => !(r.passed ?? r.success ?? true)).length;
  const totalRun = Object.keys(results).length;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <TestTube className="h-6 w-6" /> Security Tests
          </h1>
          <p className="text-muted-foreground mt-1">
            Validate Partner API security controls and integrations
          </p>
        </div>
        <Button onClick={runAllTests} disabled={isRunningAll || runningTests.size > 0}>
          {isRunningAll ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running...
            </>
          ) : (
            <>
              <Play className="h-4 w-4 mr-2" /> Run All Tests
            </>
          )}
        </Button>
      </div>

      {/* Summary Bar (shown after running tests) */}
      {totalRun > 0 && (
        <Card>
          <CardContent className="py-3">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span className="text-sm font-medium">{passedCount} passed</span>
              </div>
              {failedCount > 0 && (
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-500" />
                  <span className="text-sm font-medium">{failedCount} failed</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-muted-foreground">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">{TESTS.length - totalRun} remaining</span>
              </div>
              <div className="ml-auto">
                <div className="flex gap-1">
                  {TESTS.map((test) => {
                    const status = getTestStatus(test.id);
                    return (
                      <div
                        key={test.id}
                        className={cn(
                          "h-2 w-6 rounded-full",
                          status === "passed" && "bg-green-500",
                          status === "failed" && "bg-red-500",
                          status === "running" && "bg-amber-500 animate-pulse",
                          status === "idle" && "bg-muted"
                        )}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Test Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {TESTS.map((test) => {
          const status = getTestStatus(test.id);
          const result = results[test.id];

          return (
            <Card
              key={test.id}
              className={cn(
                "transition-colors",
                status === "passed" && "border-green-500/30",
                status === "failed" && "border-red-500/30"
              )}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn("p-2 rounded-lg", test.bgColor)}>
                      <test.icon className={cn("h-5 w-5", test.color)} />
                    </div>
                    <div>
                      <CardTitle className="text-base">{test.title}</CardTitle>
                      <CardDescription className="text-xs mt-0.5">{test.description}</CardDescription>
                    </div>
                  </div>
                  {status === "passed" && <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />}
                  {status === "failed" && <XCircle className="h-5 w-5 text-red-500 shrink-0" />}
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={status === "running" || isRunningAll}
                    onClick={() =>
                      runTestMutation.mutate({ id: test.id, endpoint: test.endpoint })
                    }
                  >
                    {status === "running" ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Running...
                      </>
                    ) : (
                      <>
                        <Play className="h-3.5 w-3.5 mr-1.5" /> Run Test
                      </>
                    )}
                  </Button>
                  {result && (
                    <span className="text-xs text-muted-foreground max-w-[200px] truncate">
                      {result.message || ((result.passed ?? result.success) ? "All checks passed" : "Some checks failed")}
                    </span>
                  )}
                </div>

                {/* Detailed results */}
                {result?.results && Array.isArray(result.results) && result.results.length > 0 && (
                  <div className="mt-3 space-y-1.5 border-t pt-3">
                    {result.results.map((r: any, i: number) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        {r.passed ?? r.success ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
                        ) : (
                          <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                        )}
                        <span className="text-muted-foreground truncate">
                          {r.name || r.test || r.message || `Check ${i + 1}`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
